import { inject, Injectable, signal } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import { GEOLOCATION_ZOOM } from '~constants/map.constants';
import { Station, StationBrand } from '~models/station.model';
import { NotificationService } from '~services/notification.service';
import { BusinessUnit } from '~shared/business-unit';
import { exhaustiveCheckWarn } from '~utils/exhaustive-check';
import { getStationFullAddress } from '~utils/station-name';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private readonly notificationService = inject(NotificationService);
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  readonly mapInitialized = signal(false);
  readonly mapConfig = signal(this.getMapConfigPerBu(BusinessUnit.Mock));
  readonly googleMap = signal<google.maps.Map | undefined>(undefined);
  readonly navigationLoading = signal(false); // TODO: Wait for a Loader component: LDSW-1268

  getCustomMarkers(
    stations: Station[],
    selectedStationId?: string | null,
  ): google.maps.marker.AdvancedMarkerElementOptions[] {
    if (!this.mapInitialized()) return [];

    return stations.map((station) => {
      const title = getStationFullAddress(station);
      const content = this.createPinElement(station.id === selectedStationId, title, station.brand);

      return {
        position: { lat: station.latitude, lng: station.longitude },
        content,
        title,
      };
    });
  }

  getMapConfigPerBu(bu: BusinessUnit): google.maps.MapOptions {
    switch (bu) {
      case BusinessUnit.US:
        return { ...getCommonConfig(), center: US_CONFIG };
      case BusinessUnit.PL:
        return { ...getCommonConfig(), center: PL_CONFIG };
      case BusinessUnit.NO:
        return { ...getCommonConfig(), center: NO_CONFIG };
      case BusinessUnit.SE:
        return { ...getCommonConfig(), center: SE_CONFIG };
      case BusinessUnit.DK:
        return { ...getCommonConfig(), center: DK_CONFIG };
      case BusinessUnit.LV:
        return { ...getCommonConfig(), center: LV_CONFIG };
      case BusinessUnit.LT:
        return { ...getCommonConfig(), center: LT_CONFIG };
      case BusinessUnit.EE:
        return { ...getCommonConfig(), center: EE_CONFIG };
      case BusinessUnit.CA:
        return { ...getCommonConfig(), center: CA_CONFIG };
      case BusinessUnit.DE:
        return { ...getCommonConfig(), center: DE_CONFIG };
      case BusinessUnit.BE:
        return { ...getCommonConfig(), center: BE_CONFIG };
      case BusinessUnit.LU:
        return { ...getCommonConfig(), center: LU_CONFIG };
      case BusinessUnit.NL:
        return { ...getCommonConfig(), center: NL_CONFIG };
      case BusinessUnit.Mock:
        return { ...getCommonConfig(), center: MOCK_CONFIG };
      default:
        exhaustiveCheckWarn(bu);

        return { ...getCommonConfig(), center: US_CONFIG };
    }
  }
  /**
   * Changes the center of the map using google.maps.Map.panTo function, and sets map's zoom using setZoom
   * @param center - center of the map, described with google.maps.LatLngLiteral
   * @param zoom - zoom of the Map, a positive integer
   */
  navigateOnDemand(center: google.maps.LatLngLiteral, zoom = 12): void {
    const map = this.googleMap();

    if (!map) {
      console.warn('Tried navigating before map was available');

      return;
    }

    map.panTo(center);
    map.setZoom(zoom);
  }

  navigateToUserPosition(): void {
    this.navigationLoading.set(true);
    this.getUserPosition((position) => {
      const { latitude, longitude } = position.coords;

      this.navigateOnDemand({ lat: latitude, lng: longitude }, GEOLOCATION_ZOOM);
      this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.GeoLocating, { geo_locating: 'consent' });

      this.navigationLoading.set(false);
    });
  }

  onMapInitialized(mapElement: GoogleMap): void {
    this.googleMap.set(mapElement.googleMap);
    this.mapInitialized.set(true);
    this.mapConfig.update((config) => ({
      ...config,
      mapTypeControlOptions: { position: google?.maps?.ControlPosition.BOTTOM_LEFT },
    }));
  }

  getUserPosition(successCallback: PositionCallback): void {
    return navigator.geolocation.getCurrentPosition(successCallback, () => {
      this.notificationService.error('geolocationDenied', 'geolocationDeniedSubtitle');
      this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.GeoLocating, { geo_locating: 'decline' });
      /* geolocation is available, but user did not grant access */
      this.navigationLoading.set(false);
    });
  }
  private createPinElement(isSelected: boolean, title: string, brand?: StationBrand): HTMLElement {
    const glyphImg = document.createElement('img');
    const imageSrc = this.getPinImage(isSelected, brand);

    glyphImg.src = new URL(imageSrc, import.meta.url).href;
    glyphImg.alt = title;

    return glyphImg;
  }
  private getPinImage(isSelected: boolean, brand: StationBrand = StationBrand.CIRCLEK): string {
    const mapPinsAssetsPath = '/assets/map-pins';

    switch (brand) {
      case StationBrand.CIRCLEK:
        return isSelected ? `${mapPinsAssetsPath}/circlek-pin-big.svg` : `${mapPinsAssetsPath}/circlek-pin.svg`;
      case StationBrand.COUCHE_TARD:
        return isSelected ? `${mapPinsAssetsPath}/couche-tard-pin-big.svg` : `${mapPinsAssetsPath}/couche-tard-pin.svg`;
      case undefined:
      default:
        exhaustiveCheckWarn(brand);

        return `${mapPinsAssetsPath}/circlek-pin.svg`;
    }
  }
}

const getCommonConfig = (): google.maps.MapOptions => {
  const controlPosition = globalThis.google?.maps?.ControlPosition;

  return {
    gestureHandling: 'greedy',
    zoom: 12,
    ...(controlPosition
      ? {
          mapTypeControlOptions: {
            position: controlPosition.BOTTOM_LEFT,
          },
        }
      : {}),
  };
};

const MOCK_CONFIG: google.maps.LatLngLiteral = { lat: 39.9525839, lng: -75.1652215 };

const US_CONFIG: google.maps.LatLngLiteral = { lat: 38.893595, lng: -77.01458 };

const PL_CONFIG: google.maps.LatLngLiteral = { lat: 52.2297, lng: 21.0122 };

const NO_CONFIG: google.maps.LatLngLiteral = { lat: 59.9139, lng: 10.7522 };

const SE_CONFIG: google.maps.LatLngLiteral = { lat: 59.3293, lng: 18.0686 };

const DK_CONFIG: google.maps.LatLngLiteral = { lat: 55.6761, lng: 12.5683 };

const LV_CONFIG: google.maps.LatLngLiteral = { lat: 56.9496, lng: 24.1052 };

const LT_CONFIG: google.maps.LatLngLiteral = { lat: 54.6872, lng: 25.2797 };

const EE_CONFIG: google.maps.LatLngLiteral = { lat: 59.437, lng: 24.7536 };

const CA_CONFIG: google.maps.LatLngLiteral = { lat: 45.424721, lng: -75.695 };

const DE_CONFIG: google.maps.LatLngLiteral = { lat: 52.52, lng: 13.405 };

const BE_CONFIG: google.maps.LatLngLiteral = { lat: 50.8503, lng: 4.3517 };

const LU_CONFIG: google.maps.LatLngLiteral = { lat: 49.6117, lng: 6.1319 };

const NL_CONFIG: google.maps.LatLngLiteral = { lat: 52.3676, lng: 4.9041 };
